import { getOne, getAll, post, put, deleteOne } from './CommonServices/httpService'
import type { FormSubmissionRequest } from '../types/Form/FormSubmission'
import {
	type CreateWorkActivityRequest,
	type UpdateWorkActivityRequest,
	type WorkActivityUpdatePayload,
} from '../types/Work/WorkEntry'
import type {
	WorkScopeRegisterFilters,
	WorkScopeRegisterPage,
} from '../types/Work/WorkScopeRegister'

export const getNextWsrTagNumber = async (projectOptionWorkTypeId: number) => {
	const response = await getOne<number>(
		`/WSR/Tag?projectOptionWorkTypeId=${projectOptionWorkTypeId}`,
	)
	return response.data
}

export type WsrTagNumberSearchResult = {
	scaffoldTagId: number
	projectName: string | null
	projectNumber: string | null
	tagNumber: number
	activityCount: number
}

export const searchWsrTagNumber = async (projectOption?: string) => {
	const response = await getAll<WsrTagNumberSearchResult[]>(
		`/WSR/TagNumbers${projectOption ? `?projectOption=${encodeURIComponent(projectOption)}` : ''}`,
	)
	return response.data
}

export const getWsrByTagId = async (scaffoldTagId: number) => {
	const response = await getOne<FormSubmissionRequest>(`/WSR/Tag/${scaffoldTagId}`)
	return response.data
}

export const createWorkActivity = async (request: CreateWorkActivityRequest) => {
	const response = await post(`/WSR/Activity`, request)
	return response.success
}

function normalizeActivityPercentForUi(value: number | null | undefined): number | null {
	if (value === null || value === undefined) return null
	const n = Number(value)
	if (!Number.isFinite(n)) return null
	if (n > 0 && n < 1) return n * 100
	return n
}

export const getWorkActivityRecords = async (
	scaffoldTagId: number,
): Promise<UpdateWorkActivityRequest[]> => {
	const response = await getAll<UpdateWorkActivityRequest[]>(`/WSR/Activity/${scaffoldTagId}`)
	return response.data.map((record) => ({
		...record,
		details: record.details ?? '',
		percentComplete: normalizeActivityPercentForUi(record.percentComplete) ?? null,
		billingLogEntryId: record.billingLogEntryId ?? null,
	}))
}

export const getWsrRegister = async (
	page: number,
	pageSize: number,
	search: string,
	filters: WorkScopeRegisterFilters,
): Promise<WorkScopeRegisterPage> => {
	const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
	if (search.trim()) params.append('search', search.trim())
	filters.projects.forEach((v) => params.append('projects', v))
	filters.workOrders.forEach((v) => params.append('workOrders', v))
	filters.purchaseOrders.forEach((v) => params.append('purchaseOrders', v))
	filters.statuses.forEach((v) => params.append('statuses', v))
	if (filters.hasActivitiesOnly) params.append('hasActivitiesOnly', 'true')

	const response = await getAll<WorkScopeRegisterPage>(`/WSR/Register?${params.toString()}`)
	return {
		...response.data,
		items: response.data.items.map((record) => ({
			...record,
			// A tag saved without coordinates is stored as 0, 0.
			latitude: record.latitude || record.longitude ? record.latitude : null,
			longitude: record.latitude || record.longitude ? record.longitude : null,
			createdBy: record.createdBy || '—',
		})),
	}
}

export const updateWorkActivity = async (payload: WorkActivityUpdatePayload) => {
	await put(`/WSR/Activity`, payload)
}

export const deleteWorkActivity = async (workActivityRegisterId: number) => {
	await deleteOne(`/WSR/Activity/${workActivityRegisterId}`)
}

export function formatWsrTagLabel(
	tag: Pick<WsrTagNumberSearchResult, 'projectName' | 'projectNumber' | 'tagNumber'>,
): string {
	const project = [tag.projectNumber, tag.projectName].filter(Boolean).join(' - ')
	return project ? `${project} · #${tag.tagNumber}` : `#${tag.tagNumber}`
}
